import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Calendar } from './ui/calendar';

/**
 * DateRangePicker — two calendar popovers (start + end) with dark theme.
 *
 * Props:
 *   startDate:  'YYYY-MM-DD'
 *   endDate:    'YYYY-MM-DD'
 *   onChange:   ({startDate, endDate}) => void
 *   presets?:   boolean — show 7/14/30-day shortcut buttons
 *   testIdPrefix?: string
 */
export function DateRangePicker({
  startDate, endDate,
  onChange,
  presets = true,
  testIdPrefix = 'daterange',
}) {
  const [openStart, setOpenStart] = useState(false);
  const [openEnd,   setOpenEnd]   = useState(false);

  const parseYMD = (s) => {
    if (!s) return undefined;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const toYMD = (d) => {
    if (!d) return '';
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const fmtLabel = (s) => {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  };

  const applyPreset = (days) => {
    const e = new Date(); e.setDate(e.getDate() - 1);
    const s = new Date(e); s.setDate(s.getDate() - (days - 1));
    onChange({ startDate: toYMD(s), endDate: toYMD(e) });
  };

  const styles = {
    wrapper: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
    trigger: {
      padding: '8px 12px',
      background: '#1e293b',
      border: '1px solid #334155',
      color: '#f1f5f9',
      borderRadius: 6,
      fontSize: 13,
      cursor: 'pointer',
      minWidth: 140,
      display: 'flex', alignItems: 'center', gap: 8,
    },
    iconBox: { color: '#94a3b8', display: 'inline-flex' },
    popover: {
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      zIndex: 1000,
      padding: 0,
      color: '#f1f5f9',
    },
    separator: { color: '#94a3b8', fontSize: 14 },
    preset: (active) => ({
      background: active ? '#60a5fa' : '#273449',
      border: `1px solid ${active ? '#60a5fa' : '#334155'}`,
      color: active ? '#0f172a' : '#f1f5f9',
      padding: '6px 12px',
      borderRadius: 6,
      fontSize: 12,
      cursor: 'pointer',
      fontWeight: active ? 600 : 400,
    }),
  };

  const CalIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );

  return (
    <div style={styles.wrapper}>
      {/* Start date */}
      <Popover.Root open={openStart} onOpenChange={setOpenStart}>
        <Popover.Trigger asChild>
          <button style={styles.trigger} data-testid={`${testIdPrefix}-start-trigger`}>
            <span style={styles.iconBox}><CalIcon /></span>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>Desde:</span>
            <span>{fmtLabel(startDate)}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={4} style={styles.popover}>
            <div className="smc-dark-calendar">
              <Calendar
                mode="single"
                selected={parseYMD(startDate)}
                defaultMonth={parseYMD(startDate)}
                onSelect={(d) => {
                  if (d) {
                    const ymd = toYMD(d);
                    // If new start > end, also push end forward
                    const nextEnd = endDate && ymd > endDate ? ymd : endDate;
                    onChange({ startDate: ymd, endDate: nextEnd });
                    setOpenStart(false);
                  }
                }}
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <span style={styles.separator}>→</span>

      {/* End date */}
      <Popover.Root open={openEnd} onOpenChange={setOpenEnd}>
        <Popover.Trigger asChild>
          <button style={styles.trigger} data-testid={`${testIdPrefix}-end-trigger`}>
            <span style={styles.iconBox}><CalIcon /></span>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>Hasta:</span>
            <span>{fmtLabel(endDate)}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={4} style={styles.popover}>
            <div className="smc-dark-calendar">
              <Calendar
                mode="single"
                selected={parseYMD(endDate)}
                defaultMonth={parseYMD(endDate)}
                disabled={(d) => startDate ? toYMD(d) < startDate : false}
                onSelect={(d) => {
                  if (d) {
                    onChange({ startDate, endDate: toYMD(d) });
                    setOpenEnd(false);
                  }
                }}
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {presets && (
        <>
          <button onClick={() => applyPreset(7)}  style={styles.preset(false)} data-testid={`${testIdPrefix}-preset-7`}>
            7 días
          </button>
          <button onClick={() => applyPreset(14)} style={styles.preset(false)} data-testid={`${testIdPrefix}-preset-14`}>
            14 días
          </button>
          <button onClick={() => applyPreset(30)} style={styles.preset(false)} data-testid={`${testIdPrefix}-preset-30`}>
            30 días
          </button>
        </>
      )}
    </div>
  );
}

export default DateRangePicker;
